/**
 * VozidlaSpravaPage — admin tools of the Vozidla module (access overhaul
 * Stage 5: Management dissolution).
 *
 * One TanStack route at `/vozidla/sprava` hosts the module-owned admin
 * workspaces that used to live under `/management?tab=…`, dispatched by the
 * `?tab` query param (same pattern as the old ManagementOverviewPage):
 *   `?tab` absent / `ceniky` — ceník editor (entity + items)
 *   `?tab=spz-pairing`       — SPZ backfill from handover protocols
 *   `?tab=lms`               — LMS catalog diff/sync
 *   `?tab=lms-explorer`      — raw LMS catalog explorer
 *   `?tab=lms-sync`          — LMS lead pull + masterdata cache sync
 *   `?tab=historie`          — change history (contracts; shared HistorieWorkspace)
 *
 * Gating mirrors the API (`/v1/management/*` vehicle tools now require
 * vozidla:admin): the page shows an access notice once membership resolves
 * without `can('vozidla', 'admin')`. Until it resolves we render optimistically
 * (same philosophy as AppRail / CommandPalette) — the server is the boundary.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Shell } from '@/components/Shell';
import { Subtabs } from '@/components/Subtabs';
import { useModuleAccess } from '@/lib/permissions';
import { useMgmtBrandsQuery } from '@/modules/management/api';
import { CenikyWorkspace } from './CenikyWorkspace';
import { SpzPairingWorkspace } from './SpzPairingWorkspace';
import { LmsCatalogWorkspace } from './LmsCatalogWorkspace';
import { LmsCatalogExplorer } from './LmsCatalogExplorer';
import { LmsSyncWorkspace } from './LmsSyncWorkspace';
import { FleetPolicyWorkspace } from './FleetPolicyWorkspace';
import { HistorieWorkspace } from '@/modules/historie/HistorieWorkspace';

export type VozidlaSpravaTab =
  | 'ceniky'
  | 'spz-pairing'
  | 'fleet-policy'
  | 'lms'
  | 'lms-explorer'
  | 'lms-sync'
  | 'historie';

export interface VozidlaSpravaSearch {
  tab?: VozidlaSpravaTab;
}

export function parseVozidlaSpravaSearch(
  input: Record<string, unknown>,
): VozidlaSpravaSearch {
  const raw = input.tab;
  const tab: VozidlaSpravaTab | undefined =
    raw === 'ceniky' ||
    raw === 'spz-pairing' ||
    raw === 'fleet-policy' ||
    raw === 'lms' ||
    raw === 'lms-explorer' ||
    raw === 'lms-sync' ||
    raw === 'historie'
      ? raw
      : undefined;
  return tab ? { tab } : {};
}

const TAB_OPTIONS: { value: VozidlaSpravaTab; label: string }[] = [
  { value: 'ceniky', label: 'Ceníky' },
  { value: 'spz-pairing', label: 'Párování SPZ' },
  { value: 'fleet-policy', label: 'Fleet policy' },
  { value: 'lms', label: 'LMS katalog' },
  { value: 'lms-explorer', label: 'LMS průzkum' },
  { value: 'lms-sync', label: 'LMS sync' },
  { value: 'historie', label: 'Historie' },
];

const TAB_LABEL: Record<VozidlaSpravaTab, string> = {
  ceniky: 'Ceníky',
  'spz-pairing': 'Párování SPZ',
  'fleet-policy': 'Fleet policy',
  lms: 'LMS katalog',
  'lms-explorer': 'LMS průzkum',
  'lms-sync': 'LMS sync',
  historie: 'Historie',
};

export function VozidlaSpravaPage() {
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: '/vozidla/sprava' }) as VozidlaSpravaSearch;
  const activeTab: VozidlaSpravaTab = urlSearch.tab ?? 'ceniky';

  const { can, resolved } = useModuleAccess();
  // Optimistic until membership resolves — the API is the hard boundary.
  const canAdmin = !resolved || can('vozidla', 'admin');

  const brands = useMgmtBrandsQuery();

  const onTabChange = (next: VozidlaSpravaTab) => {
    if (next === activeTab) return;
    if (next === 'ceniky') {
      navigate({ to: '/vozidla/sprava', search: {}, replace: true });
    } else {
      navigate({ to: '/vozidla/sprava', search: { tab: next }, replace: true });
    }
  };

  return (
    <Shell rail={<AppRail active="vozidla" />}>
      <Crown>
        <Breadcrumb
          segments={[
            { label: 'Vozidla', href: '/vozidla' },
            { label: 'Správa' },
            { label: TAB_LABEL[activeTab] },
          ]}
        />
        <Crown.Row className="mt-3">
          <DisplayHeading>Správa</DisplayHeading>
          <CrownMeta
            items={[
              {
                label: 'Značek v ceníku',
                value: brands.isLoading ? '—' : brands.data?.brands.length ?? 0,
              },
            ]}
          />
        </Crown.Row>
        <Subtabs<VozidlaSpravaTab>
          aria-label="Správa vozidel — nástroje"
          value={activeTab}
          onChange={onTabChange}
          items={TAB_OPTIONS}
        />
      </Crown>

      {!canAdmin ? (
        <div
          data-testid="vozidla-sprava-denied"
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5"
        >
          <EmptyState
            eyebrow="403"
            title="Správa vozidel vyžaduje administrátorský přístup k modulu."
            description="Pro přístup požádejte správce firmy."
          />
        </div>
      ) : activeTab === 'historie' ? (
        <HistorieWorkspace moduly={['vozidla']} />
      ) : activeTab === 'spz-pairing' ? (
        <SpzPairingWorkspace isAdmin={canAdmin} />
      ) : activeTab === 'fleet-policy' ? (
        <FleetPolicyWorkspace isAdmin={canAdmin} />
      ) : activeTab === 'lms' ? (
        <LmsCatalogWorkspace isAdmin={canAdmin} />
      ) : activeTab === 'lms-explorer' ? (
        <LmsCatalogExplorer isAdmin={canAdmin} />
      ) : activeTab === 'lms-sync' ? (
        <LmsSyncWorkspace />
      ) : (
        <CenikyWorkspace brands={brands.data?.brands ?? []} brandsLoading={brands.isLoading} />
      )}
    </Shell>
  );
}
